import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  TrendingUp, BarChart3, Settings, LogOut, Check, CreditCard, Tag, Brain, HelpCircle, Shield
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

import API from '../utils/api';

const SubscriptionPage = () => {
  const { t, language } = useLanguage();
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { valid, discount_percent, original_price, discounted_price }

  // Fetch pricing from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API}/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // Calculate display price based on billing interval
  const getDisplayPrice = (planId) => {
    const defaults = { basic: 8.99, pro: 14.99, premium: 25.99 };
    if (!pricing || !pricing[planId]) {
      const base = defaults[planId];
      if (billingInterval === 'lifetime') {
        return planId === 'premium'
          ? { price: 499.99, isLifetime: true, hasDiscount: false }
          : { price: base, originalPrice: null, hasDiscount: false, notAvailable: true };
      }
      if (billingInterval === 'year') {
        const yearlyTotal = base * 12;
        return { price: base, yearlyTotal, monthlyEquivalent: base, originalPrice: null, hasDiscount: false, savings: 0 };
      }
      return { price: base, originalPrice: null, hasDiscount: false };
    }
    const plan = pricing[planId];
    const hasMonthlyDiscount = plan.discount_active && plan.discount_percent > 0;
    const monthlyPrice = hasMonthlyDiscount
      ? parseFloat((plan.price * (1 - plan.discount_percent / 100)).toFixed(2))
      : plan.price;

    if (billingInterval === 'lifetime') {
      if (planId === 'premium') {
        const lifetimePrice = plan.lifetime_price || 499.99;
        return { price: lifetimePrice, isLifetime: true, hasDiscount: false };
      }
      return { price: monthlyPrice, originalPrice: null, hasDiscount: false, notAvailable: true };
    }

    if (billingInterval === 'year') {
      const yearlyDiscount = plan.yearly_discount_percent || 0;
      const fullYearlyPrice = monthlyPrice * 12;
      const yearlyTotal = parseFloat((fullYearlyPrice * (1 - yearlyDiscount / 100)).toFixed(2));
      const monthlyEquivalent = parseFloat((yearlyTotal / 12).toFixed(2));
      const savings = parseFloat((fullYearlyPrice - yearlyTotal).toFixed(2));
      return {
        price: monthlyEquivalent,
        yearlyTotal,
        monthlyEquivalent,
        originalPrice: yearlyDiscount > 0 ? monthlyPrice : null,
        hasDiscount: yearlyDiscount > 0,
        discountPercent: yearlyDiscount,
        savings,
      };
    }
    return {
      price: monthlyPrice,
      originalPrice: hasMonthlyDiscount ? plan.price : null,
      hasDiscount: hasMonthlyDiscount,
      discountPercent: plan.discount_percent,
    };
  };

  const formatPrice = (price) => {
    return price.toFixed(2).replace('.', ',');
  };

  // Reset promo when switching billing interval
  useEffect(() => {
    setPromoCode('');
    setPromoResult(null);
  }, [billingInterval]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await fetch(`${API}/paypal/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ promo_code: promoCode.trim() }),
      });
      const text = await res.text();
      if (res.ok) {
        const data = JSON.parse(text);
        setPromoResult(data);
        toast.success(language === 'de' ? `Promo-Code angewendet: -${data.discount_percent}%` : `Promo applied: -${data.discount_percent}%`);
      } else {
        let detail = language === 'de' ? 'Ungültiger Promo-Code' : 'Invalid promo code';
        try { detail = JSON.parse(text).detail || detail; } catch {}
        setPromoResult(null);
        toast.error(detail);
      }
    } catch {
      toast.error(language === 'de' ? 'Verbindungsfehler' : 'Connection error');
      setPromoResult(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
  };

  // Features matching Landing Page exactly
  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      features: [
        '1 Trading-Konto',
        '1 Trading-Strategie',
        'CSV-Import',
        'Dashboard-Statistiken',
        'Trading-Kalender'
      ],
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      features: [
        '5 Trading-Konten',
        '5 Trading-Strategien',
        'Alles aus Basic',
        'Screenshot-Upload mit KI',
        'Notizen & Emotionen',
        'PDF-Export'
      ],
      highlighted: true,
    },
    {
      id: 'premium',
      name: 'Premium',
      features: [
        'Unbegrenzte Konten',
        'Unbegrenzte Strategien',
        'Alles aus Pro',
        'KI Trading-Mentor',
        'Skill-Score System',
        'Premium-Support'
      ],
      highlighted: false,
    },
  ];

  // Check payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await fetch(`${API}/subscription/status/${sessionId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          await checkAuth();
          setCheckingPayment(false);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
          setCheckingPayment(false);
          return;
        }
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setPaymentStatus('error');
      setCheckingPayment(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          billing_interval: billingInterval,
        }),
      });
      const text = await response.text();
      if (response.ok) {
        const data = JSON.parse(text);
        window.location.href = data.url;
      } else {
        let detail = 'Error creating checkout';
        try { detail = JSON.parse(text).detail || detail; } catch {}
        toast.error(
          language === 'de' ? 'Fehler beim Checkout' : 'Checkout Error',
          { description: detail, duration: 5000 }
        );
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(language === 'de' ? 'Verbindungsfehler' : 'Connection error');
    } finally {
      setLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Sidebar - Full menu like Settings page */}
        <aside className="fixed left-0 top-0 h-full w-16 bg-zinc-900 border-r border-zinc-800 flex flex-col items-center py-4 z-50">
          <Link to="/dashboard" className="mb-8">
            <TrendingUp className="h-8 w-8 text-primary" />
          </Link>
          <nav className="flex-1 flex flex-col gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/dashboard" className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                  <BarChart3 className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{t('dashboard')}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/mentor" className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                  <Brain className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">KI Mentor</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/support" className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                  <HelpCircle className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Support</TooltipContent>
            </Tooltip>
            {user?.is_admin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/admin/dashboard" className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                    <Shield className="h-5 w-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Admin</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/settings" className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                  <Settings className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{t('settings')}</TooltipContent>
            </Tooltip>
          </nav>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handleLogout} className="p-3 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors">
                <LogOut className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{t('logout')}</TooltipContent>
          </Tooltip>
        </aside>

        {/* Main Content */}
        <main className="ml-16 p-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold mb-2">{t('choosePlan')}</h1>
              <p className="text-muted-foreground mb-6">
                {billingInterval === 'month' ? t('monthly') : billingInterval === 'year' ? (language === 'de' ? 'Jährliche Abrechnung' : 'Billed annually') : (language === 'de' ? 'Einmalzahlung' : 'One-time payment')}
              </p>
              
              {/* Billing Toggle */}
              <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-full px-2 py-1.5" data-testid="billing-toggle-subscription">
                <button
                  onClick={() => setBillingInterval('month')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    billingInterval === 'month'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="sub-billing-monthly-btn"
                >
                  {language === 'de' ? 'Monatlich' : 'Monthly'}
                </button>
                <button
                  onClick={() => setBillingInterval('year')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    billingInterval === 'year'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="sub-billing-yearly-btn"
                >
                  {language === 'de' ? 'Jährlich' : 'Yearly'}
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-bold">
                    {language === 'de' ? 'Spare mehr' : 'Save more'}
                  </span>
                </button>
                <button
                  onClick={() => setBillingInterval('lifetime')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    billingInterval === 'lifetime'
                      ? 'bg-amber-500 text-black shadow-sm'
                      : 'text-zinc-400 hover:text-amber-400'
                  }`}
                  data-testid="sub-billing-lifetime-btn"
                >
                  Lifetime
                </button>
              </div>
            </div>

            {/* Payment Status Messages */}
            {checkingPayment && (
              <Card className="bg-primary/10 border-primary/20 mb-6">
                <CardContent className="py-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                  <p>{language === 'de' ? 'Zahlung wird verarbeitet...' : 'Processing payment...'}</p>
                </CardContent>
              </Card>
            )}

            {paymentStatus === 'success' && (
              <Card className="bg-emerald-500/10 border-emerald-500/20 mb-6">
                <CardContent className="py-4 text-center text-emerald-400">
                  <Check className="h-6 w-6 mx-auto mb-2" />
                  <p>{language === 'de' ? 'Zahlung erfolgreich! Dein Abo ist jetzt aktiv.' : 'Payment successful! Your subscription is now active.'}</p>
                </CardContent>
              </Card>
            )}

            {paymentStatus === 'error' && (
              <Card className="bg-destructive/10 border-destructive/20 mb-6">
                <CardContent className="py-4 text-center text-destructive">
                  <p>{language === 'de' ? 'Fehler bei der Zahlung. Bitte versuche es erneut.' : 'Payment error. Please try again.'}</p>
                </CardContent>
              </Card>
            )}

            {/* Current Plan Info */}
            {(user?.subscription_status === 'active' || user?.subscription_status === 'lifetime') && (
              <Card className={`mb-6 ${user?.subscription_status === 'lifetime' ? 'glow-gold bg-zinc-900/50 border-amber-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className={`h-5 w-5 ${user?.subscription_status === 'lifetime' ? 'text-amber-400' : 'text-primary'}`} />
                    <div>
                      <p className="font-medium">
                        {t('currentPlan')}: <span className={`capitalize ${user?.subscription_status === 'lifetime' ? 'text-amber-400' : 'text-primary'}`}>{user.subscription_plan}{user?.subscription_status === 'lifetime' ? ' Lifetime' : ''}</span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user?.subscription_status === 'lifetime'
                          ? (language === 'de' ? 'Lebenslanger Premium-Zugang' : 'Lifetime Premium access')
                          : (language === 'de' ? 'Dein Abo ist aktiv' : 'Your subscription is active')
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const isCurrentPlan = user?.subscription_plan === plan.id && (user?.subscription_status === 'active' || user?.subscription_status === 'lifetime');
                const priceInfo = getDisplayPrice(plan.id);
                const isLifetimePremium = billingInterval === 'lifetime' && plan.id === 'premium';
                const isLifetimeOther = billingInterval === 'lifetime' && plan.id !== 'premium';
                
                return (
                  <Card 
                    key={plan.id}
                    className={`relative transition-all duration-300 ${
                      isLifetimePremium
                        ? 'glow-gold bg-zinc-900 scale-105'
                        : isLifetimeOther
                          ? 'bg-zinc-900/30 border-zinc-800 opacity-60'
                          : plan.highlighted
                            ? 'border-primary bg-zinc-900 scale-105'
                            : 'bg-zinc-900/50 border-zinc-800'
                    }`}
                  >
                    {plan.highlighted && !isLifetimePremium && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                        Beliebt
                      </div>
                    )}
                    {isLifetimePremium && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full" data-testid="sub-lifetime-badge">
                        Lifetime Deal
                      </div>
                    )}
                    {plan.id === 'premium' && !isLifetimePremium && (
                      <div className="absolute -top-3 right-4 bg-gradient-to-r from-emerald-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Brain className="h-3 w-3" />
                        KI Mentor
                      </div>
                    )}
                    {priceInfo.hasDiscount && !priceInfo.isLifetime && (
                      <div className="absolute -top-3 left-4 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        -{priceInfo.discountPercent}%
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <div className="mt-4">
                        {priceInfo.isLifetime ? (
                          <>
                            {promoResult ? (
                              <>
                                <span className="text-lg text-muted-foreground line-through mr-2">
                                  {formatPrice(promoResult.original_price)}€
                                </span>
                                <span className="text-4xl font-heading font-bold text-amber-400">
                                  {formatPrice(promoResult.discounted_price)}€
                                </span>
                              </>
                            ) : (
                              <span className="text-4xl font-heading font-bold text-amber-400">
                                {formatPrice(priceInfo.price)}€
                              </span>
                            )}
                            <span className="text-muted-foreground ml-1">{language === 'de' ? 'einmalig' : 'one-time'}</span>
                            {promoResult && (
                              <p className="text-xs text-emerald-400 mt-1 font-semibold" data-testid="promo-discount-display">
                                -{promoResult.discount_percent}% Rabatt
                              </p>
                            )}
                            <p className="text-xs text-amber-400/80 mt-1 font-medium">{language === 'de' ? 'Für immer Premium' : 'Premium forever'}</p>
                          </>
                        ) : (
                          <>
                            {priceInfo.hasDiscount && priceInfo.originalPrice && (
                              <span className="text-lg text-muted-foreground line-through mr-2">
                                {formatPrice(priceInfo.originalPrice)}€
                              </span>
                            )}
                            <span className={`text-4xl font-heading font-bold ${priceInfo.hasDiscount ? 'text-emerald-400' : ''}`}>
                              {formatPrice(priceInfo.price)}€
                            </span>
                            <span className="text-muted-foreground">/Monat</span>
                          </>
                        )}
                      </div>
                      {billingInterval === 'year' && priceInfo.yearlyTotal && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-muted-foreground">
                            {formatPrice(priceInfo.yearlyTotal)}€ / {language === 'de' ? 'Jahr' : 'year'}
                          </p>
                          {priceInfo.savings > 0 && (
                            <p className="text-xs font-semibold text-emerald-400" data-testid={`sub-savings-${plan.id}`}>
                              {language === 'de' ? 'Du sparst' : 'You save'} {formatPrice(priceInfo.savings)}€
                            </p>
                          )}
                        </div>
                      )}
                    </CardHeader>
                    
                    <CardContent className="pt-6">
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Check className={`h-4 w-4 flex-shrink-0 ${feature.includes('KI') ? 'text-purple-400' : 'text-primary'}`} />
                            <span className={feature.includes('KI') || feature.includes('Skill') ? 'font-medium text-purple-300' : ''}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                      
                      {isLifetimeOther ? (
                        <p className="text-xs text-center text-muted-foreground italic py-2" data-testid={`sub-lifetime-hint-${plan.id}`}>
                          {language === 'de' ? 'Lifetime nur für Premium' : 'Lifetime only for Premium'}
                        </p>
                      ) : isLifetimePremium && !isCurrentPlan ? (
                        <div className="space-y-3">
                          {/* Promo Code Input */}
                          <div className="space-y-2" data-testid="promo-code-section">
                            {promoResult ? (
                              <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Tag className="h-3.5 w-3.5 text-emerald-400" />
                                  <span className="text-sm font-mono text-emerald-400 font-semibold">{promoCode.toUpperCase()}</span>
                                  <span className="text-xs text-emerald-400/80">-{promoResult.discount_percent}%</span>
                                </div>
                                <button
                                  onClick={handleRemovePromo}
                                  className="text-xs text-zinc-400 hover:text-white transition-colors"
                                  data-testid="promo-remove-btn"
                                >
                                  {language === 'de' ? 'Entfernen' : 'Remove'}
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder={language === 'de' ? 'Promo-Code' : 'Promo code'}
                                  value={promoCode}
                                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                  onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50"
                                  data-testid="promo-code-input"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleApplyPromo}
                                  disabled={promoLoading || !promoCode.trim()}
                                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs px-3"
                                  data-testid="promo-apply-btn"
                                >
                                  {promoLoading ? '...' : (language === 'de' ? 'Anwenden' : 'Apply')}
                                </Button>
                              </div>
                            )}
                          </div>

                          <Button 
                            className="w-full bg-gradient-to-r from-amber-500 to-yellow-400 text-black hover:from-amber-600 hover:to-yellow-500 font-bold"
                            onClick={() => handleSelectPlan(plan.id)}
                            disabled={loading === plan.id}
                            data-testid="select-plan-premium"
                          >
                            {loading === plan.id ? 'Laden...' : (
                              <>
                                <CreditCard className="h-4 w-4 mr-2" />
                                {language === 'de' ? 'Mit Karte zahlen' : 'Pay with Card'}
                              </>
                            )}
                          </Button>
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-700"></div></div>
                            <div className="relative flex justify-center"><span className="bg-zinc-900 px-2 text-xs text-zinc-500">{language === 'de' ? 'oder' : 'or'}</span></div>
                          </div>
                          <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: 'EUR', intent: 'capture', 'enable-funding': 'paylater' }}>
                            <PayPalButtons
                              style={{ layout: 'horizontal', color: 'gold', shape: 'rect', label: 'paypal', height: 40 }}
                              forceReRender={[promoResult]}
                              createOrder={async () => {
                                const body = promoResult ? { promo_code: promoCode.trim() } : {};
                                const res = await fetch(`${API}/paypal/create-order`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
                                const text = await res.text();
                                if (!res.ok) { let d = 'PayPal Error'; try { d = JSON.parse(text).detail; } catch {} toast.error(d); throw new Error(d); }
                                return JSON.parse(text).id;
                              }}
                              onApprove={async (data) => {
                                const res = await fetch(`${API}/paypal/capture-order/${data.orderID}`, { method: 'POST', credentials: 'include' });
                                const text = await res.text();
                                if (res.ok) {
                                  toast.success(language === 'de' ? 'Zahlung erfolgreich!' : 'Payment successful!');
                                  setTimeout(() => window.location.reload(), 1500);
                                } else {
                                  let d = 'Capture failed'; try { d = JSON.parse(text).detail; } catch {} toast.error(d);
                                }
                              }}
                              onError={(err) => { console.error('PayPal error:', err); toast.error('PayPal Fehler'); }}
                              data-testid="paypal-lifetime-btn"
                            />
                          </PayPalScriptProvider>
                          <p className="text-[10px] text-center text-zinc-500">
                            {language === 'de' ? 'Auch mit PayPal Ratenzahlung möglich' : 'PayPal Pay Later available'}
                          </p>
                        </div>
                      ) : (
                        <Button 
                          className={`w-full ${
                            plan.highlighted
                              ? 'glow-primary'
                              : plan.id === 'premium'
                                ? 'bg-gradient-to-r from-emerald-500 to-purple-500 hover:from-emerald-600 hover:to-purple-600'
                                : ''
                          }`}
                          variant={plan.highlighted || plan.id === 'premium' ? 'default' : 'outline'}
                          onClick={() => handleSelectPlan(plan.id)}
                          disabled={loading === plan.id || isCurrentPlan}
                          data-testid={`select-plan-${plan.id}`}
                        >
                          {isCurrentPlan 
                            ? 'Aktueller Plan'
                            : loading === plan.id 
                              ? 'Laden...' 
                              : plan.id === 'premium' ? 'Premium starten' : t('selectPlan')
                          }
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Footer Info */}
            <div className="mt-12 text-center text-sm text-muted-foreground">
              <p>
                Sichere Zahlung über Stripe. Du kannst jederzeit kündigen.
              </p>
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
};

export default SubscriptionPage;
